import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  FirebaseUser, 
  onAuthChange, 
  signInWithGoogle, 
  signInWithApple,
  signInWithOneTap,
  signInWithMicrosoft,
  sendMagicLink as firebaseSendMagicLink,
  completeMagicLinkSignIn as firebaseCompleteMagicLink,
  completeRedirectSignIn,
  signOut as firebaseSignOut,
  REDIRECT_METHOD_KEY,
  SignInMethod,
} from './firebase';
import { apiService } from '@/api';
import { useUserStore } from '@/store/userStore';
import { notificationService } from '@/services/notificationService';
import { 
  clearAnalyticsUserContext, 
  trackLogin, 
  trackLogout,
  trackSignUpOnce,
  wasSignUpTracked,
} from '@/services/analyticsService';
import { trackToltSignup } from '@/services/toltService';
import { setAuthHintCookie, clearAuthHintCookie } from './authHintCookie';
import { getSessionToken, clearSessionToken } from './sessionToken';

type AuthMethod = SignInMethod;

/** The provider a Firebase user signed in with, for a sign-up the background sync detects. */
const methodFromFirebaseUser = (fbUser: FirebaseUser, fallback: AuthMethod): AuthMethod => {
  const provider = fbUser.providerData?.[0]?.providerId || '';
  if (provider.startsWith('google')) return 'google';
  if (provider.startsWith('apple')) return 'apple';
  if (provider.startsWith('microsoft')) return 'microsoft';
  if (provider === 'password' || provider === 'emailLink') return 'email';
  return fallback;
};

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithGoogleOneTap: (idToken: string) => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLinkSignIn: (email?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

/** The person closed or cancelled the provider's window. That is an expected outcome,
 *  not an error (LoginPage shows nothing for it either), so it is not console.error'd:
 *  TrackJS reports every console.error (~290 such reports in its first 7.5 hours). */
const USER_CANCELLED_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled']);
function logSignInError(label: string, error: unknown): void {
  if (USER_CANCELLED_CODES.has((error as { code?: string } | null)?.code ?? '')) return;
  console.error(label, error);
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setUser, clearUser, setUserId } = useUserStore();

  // Sync user with backend after Firebase auth
  const syncUserWithBackend = useCallback(async (fbUser: FirebaseUser, isNewSignIn: boolean = false, method: AuthMethod = 'google') => {
    try {
      // Fallback new-user heuristic (see below) — only used if the backend
      // doesn't say whether it created the row.
      const existingUserId = localStorage.getItem('vivideo_user_id');
      
      // Get or create user in backend
      const { userId, created } = await apiService.getUserById(
        fbUser.email || '',
        fbUser.uid,
        fbUser.displayName?.split(' ')[0],
        fbUser.displayName?.split(' ').slice(1).join(' '),
        fbUser.displayName || undefined,
        fbUser.photoURL || undefined
      );

      setUserId(userId);

      // Get full user info
      const user = await apiService.getUserInfo(userId);
      // `setUser` in userStore already propagates the context to
      // setAnalyticsUserContext (which identifies the user in GA + Amplitude),
      // so we intentionally do NOT call setAnalyticsUserContext again here —
      // doing so would double-fire $identify on every sign-in.
      setUser(user);

      // Sign up vs login. The backend's 201 decides, on EITHER path: onAuthChange and the
      // sign-in handler both call get-or-create, and only the first request sees the 201.
      // The localStorage comparison stays only as a fallback for interactive sign-ins.
      const isNewAccount = created ?? (isNewSignIn && (!existingUserId || existingUserId !== userId));
      if (isNewAccount) {
        if (trackSignUpOnce(userId, isNewSignIn ? method : methodFromFirebaseUser(fbUser, method))) {
          // Convert referred visitors into Tolt leads (no-op when not referred)
          trackToltSignup(fbUser.email || '');
        }
      } else if (isNewSignIn && !wasSignUpTracked(userId)) {
        trackLogin(method);
      }

      // Store in localStorage
      localStorage.setItem('vivideo_user_id', userId);

      // Re-register FCM token if user previously granted notification permission
      notificationService.reRegisterIfNeeded(userId).catch((err) => {
        console.error('Error re-registering FCM token:', err);
      });
    } catch (error) {
      console.error('Error syncing user with backend:', error);
    }
  }, [setUser, setUserId]);

  // Listen for Firebase auth state changes
  useEffect(() => {
    // Complete any pending redirect-based OAuth sign-in on load. Firebase needs
    // getRedirectResult() to process the provider callback URL; onAuthStateChanged
    // then fires with the signed-in user and we pick up REDIRECT_METHOD_KEY.
    completeRedirectSignIn().catch((err) => {
      console.error('Error completing redirect sign-in:', err);
    });

    const unsubscribe = onAuthChange(async (user) => {
      setFirebaseUser(user);

      if (user) {
        // Tell the landing page (vivideo.ai) this browser has a session, so it can
        // auto-forward returning users here. Runs on every load → acts as a refresh.
        setAuthHintCookie();
        // A real Firebase session supersedes any email-OTP marker.
        localStorage.removeItem('vivideo_email_auth');
        clearSessionToken();
        // If we returned from a redirect-based sign-in (popup-blocked / in-app
        // browser), this auth change IS the fresh interactive sign-in — treat it
        // as such so sign_up/login fire with the right method.
        const redirectMethod = sessionStorage.getItem(REDIRECT_METHOD_KEY) as AuthMethod | null;
        if (redirectMethod) {
          sessionStorage.removeItem(REDIRECT_METHOD_KEY);
          await syncUserWithBackend(user, true, redirectMethod);
        } else {
          await syncUserWithBackend(user);
        }
      } else {
        // No Firebase session. Email-OTP logins are NOT backed by Firebase, so a
        // null here is EXPECTED for them — restore the session instead of logging
        // them out on every refresh.
        // Restored only with the backend-signed session token; a bare user id
        // (older sessions, or one typed into storage) signs the browser out.
        const sessionToken = getSessionToken();
        const emailUid = sessionToken ? localStorage.getItem('vivideo_email_auth') : null;
        if (!emailUid && sessionToken) {
          // A sign-in with this token is still completing (e.g. a staff login link).
        } else if (emailUid) {
          try {
            setUserId(emailUid);
            localStorage.setItem('vivideo_user_id', emailUid);
            const restored = await apiService.getUserInfo(emailUid);
            setUser(restored);
            // Email-OTP sessions count as logged-in for the landing's hint too.
            setAuthHintCookie();
          } catch (err) {
            // Stale/invalid session (e.g. account deleted) → clear it out.
            console.error('Email-OTP session restore failed:', err);
            clearUser();
            clearAuthHintCookie();
            clearSessionToken();
            localStorage.removeItem('vivideo_user_id');
            localStorage.removeItem('vivideo_email_auth');
          }
        } else {
          clearUser();
          clearAuthHintCookie();
          clearSessionToken();
          localStorage.removeItem('vivideo_user_id');
          localStorage.removeItem('vivideo_email_auth');
        }
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [syncUserWithBackend, clearUser, setUser, setUserId]);

  const handleSignInWithGoogle = async () => {
    setIsLoading(true);
    try {
      const user = await signInWithGoogle();
      // null → a full-page redirect was started; the sign-in completes via
      // onAuthChange on return, so there's nothing more to do here.
      if (user) await syncUserWithBackend(user, true, 'google');
    } catch (error) {
      logSignInError('Google sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInWithGoogleOneTap = async (idToken: string) => {
    setIsLoading(true);
    try {
      const user = await signInWithOneTap(idToken);
      await syncUserWithBackend(user, true, 'google');
    } catch (error) {
      logSignInError('Google One Tap sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInWithApple = async () => {
    setIsLoading(true);
    try {
      const user = await signInWithApple();
      if (user) await syncUserWithBackend(user, true, 'apple');
    } catch (error) {
      logSignInError('Apple sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInWithMicrosoft = async () => {
    setIsLoading(true);
    try {
      const user = await signInWithMicrosoft();
      if (user) await syncUserWithBackend(user, true, 'microsoft');
    } catch (error) {
      logSignInError('Microsoft sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMagicLink = async (email: string) => {
    setIsLoading(true);
    try {
      await firebaseSendMagicLink(email);
    } catch (error) {
      console.error('Send Login Link error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteMagicLinkSignIn = async (email?: string) => {
    setIsLoading(true);
    try {
      const user = await firebaseCompleteMagicLink(email);
      await syncUserWithBackend(user, true, 'email');
    } catch (error) {
      console.error('Magic link sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Track logout before clearing context
      trackLogout();
      
      await firebaseSignOut();
      clearUser();
      clearAnalyticsUserContext();
      // Explicit clear: for email-OTP sessions there is no Firebase user, so the
      // auth listener won't fire here — don't rely on it to drop the landing hint.
      clearAuthHintCookie();
      clearSessionToken();
      localStorage.removeItem('vivideo_user_id');
      localStorage.removeItem('vivideo_email_auth');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        isLoading,
        isAuthenticated: !!firebaseUser,
        signInWithGoogle: handleSignInWithGoogle,
        signInWithGoogleOneTap: handleSignInWithGoogleOneTap,
        signInWithApple: handleSignInWithApple,
        signInWithMicrosoft: handleSignInWithMicrosoft,
        sendMagicLink: handleSendMagicLink,
        completeMagicLinkSignIn: handleCompleteMagicLinkSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

